import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import * as alertActions from './alert.actions';
import * as alertSelectors from './alert.selectors';
import * as operatorsActions from '../../../store/operators/operators.actions';
import * as operatorSelectors from '../../../store/operators/operators.selectors';
import { AlertsConfigurationService } from '../services/alerts-configuration.service';
import { IncidentsService } from '../services/incidents.service';
import { MessageTranslateService } from '../../../shared/services/message.service';
import { DefaultPageSize } from '../../../shared/constants/pagination.constants';

export class AlertsEffects {
  actions$: Actions = inject(Actions);
  alertsService = inject(AlertsConfigurationService);
  incidentsService = inject(IncidentsService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  loadAlertConfigurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        alertActions.loadAlertConfigurationList,
        alertActions.changeAlertConfigurationPaginationSuccessful,
        alertActions.insertAlertConfigurationSuccess,
        alertActions.updateAlertConfigurationSuccess,
        alertActions.deleteAlertConfigurationSuccess,
        operatorsActions.SetCurrentOperatorSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(alertSelectors.selectAlertConfigurationsPagination),
        this.store.select(operatorSelectors.selectCurrentOperator),
      ]),
      switchMap(([_action, pagination, operator]) =>
        this.alertsService
          .getAlertConfigurations({
            operatorId: operator?.id,
            pageIndex: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? DefaultPageSize,
          })
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return alertActions.loadAlertConfigurationListSuccess({
                  response: res.value!,
                });
              }
              return alertActions.loadAlertConfigurationListFailure({
                error: res.error!,
              });
            }),
            catchError((err) =>
              of(
                alertActions.loadAlertConfigurationListFailure({
                  error: err.message || 'Loading alert configurations failed',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  changeAlertConfigurationPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(alertActions.changeAlertConfigurationPagination),
      map(() => alertActions.changeAlertConfigurationPaginationSuccessful()),
    ),
  );

  insertAlertConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(alertActions.insertAlertConfiguration),
      switchMap(({ request }) =>
        this.alertsService.createAlertConfiguration(request).pipe(
          map((res) => {
            if (res.isSuccess) {
              return alertActions.insertAlertConfigurationSuccess({
                result: res,
              });
            }
            return alertActions.insertAlertConfigurationFailure({
              error: res.error!,
            });
          }),
          catchError((err) =>
            of(
              alertActions.insertAlertConfigurationFailure({
                error: err.message || 'Creating alert configuration failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateAlertConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(alertActions.updateAlertConfiguration),
      switchMap(({ id, request }) =>
        this.alertsService.updateAlertConfiguration(id, request).pipe(
          map((res: any) => {
            if (res.isSuccess) {
              return alertActions.updateAlertConfigurationSuccess({
                result: res,
              });
            }
            return alertActions.updateAlertConfigurationFailure({
              error: res.error!,
            });
          }),
          catchError((err) =>
            of(
              alertActions.updateAlertConfigurationFailure({
                error: err.message || 'Updating alert configuration failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteAlertConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(alertActions.deleteAlertConfiguration),
      switchMap(({ id }) =>
        this.alertsService.deleteAlertConfiguration(id).pipe(
          map((res) => {
            if (res.isSuccess) {
              return alertActions.deleteAlertConfigurationSuccess({
                result: res,
              });
            }
            return alertActions.deleteAlertConfigurationFailure({
              error: res.error!,
            });
          }),
          catchError((err) =>
            of(
              alertActions.deleteAlertConfigurationFailure({
                error: err.message || 'Deleting alert configuration failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  onUpsertError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          alertActions.insertAlertConfigurationFailure,
          alertActions.updateAlertConfigurationFailure,
          alertActions.deleteAlertConfigurationFailure,
        ),
        tap((err) =>
          err.error
            ? this.messageService.error(err.error)
            : this.messageService.errorTranslate('alerts.failed-to-save'),
        ),
      ),
    { dispatch: false },
  );

  onDeleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(alertActions.deleteAlertConfigurationSuccess),
        tap(() =>
          this.messageService.successTranslate('alerts.list.deleted-successfully'),
        ),
      ),
    { dispatch: false },
  );
}
